import { Trans, useTranslation } from 'next-i18next/pages'
import { AncienneBelgiqueProps } from './types'
import { CallToAction, Header, HeadingAndContent, TextAndMedia } from '@boltenergy-be/design-system'
import FaqWithDefaultDescription from 'components/faq-with-default-description/FaqWithDefaultDescription'
import BoltOffering from 'sections/bolt-offering/BoltOffering'
import parse from 'html-react-parser'
import Link from 'components/link/Link'
import { useRouter } from 'next/router'
import { routes } from 'utils/routes'
import styles from './AncienneBelgique.module.scss'
import { AB_NEWS_ARTICLE_SLUG, AB_SIMULATION_SALES_OFFICE } from 'features/ancienne-belgique/constants'
import { BOLT_EMAIL } from 'constants/constants'
import { trackEvent } from 'utils/tracking'
import { CommonTrackingParams } from 'types/tracking'
import { I18nUSP } from 'types/i18n'
import StartSimulationButton from 'components/start-simulation-button/StartSimulationButton'

const AncienneBelgique = ({ faqCategories }: AncienneBelgiqueProps) => {
  // i18n
  const { t } = useTranslation(['ancienneBelgique', 'common'])

  // Router
  const { locale } = useRouter()

  // Constants
  const USPs = (t('common:usps', { returnObjects: true }) as Array<I18nUSP>).map((usp) =>
    usp.key === 'fun' ? { ...usp, description: t('uspOverride.description'), title: t('uspOverride.title') } : usp
  )

  return (
    <main>
      <Header title={t('header.title')} classnames={{ content: styles.header }} docked color="dark" shape="s1">
        <p>{parse(t('header.description'))}</p>
      </Header>

      <TextAndMedia
        inverse
        showMediaOnMobile
        title={t('energyPartner.title')}
        media={{
          type: 'video',
          props: {
            publicId: 'website/ancienne-belgique/ab-x-bolt',
            thumbnail: { publicId: 'website/ancienne-belgique/thumbnail', fill: true, alt: '' }
          }
        }}
      >
        <p>{t('energyPartner.description')}</p>
        {!!AB_NEWS_ARTICLE_SLUG[locale] && (
          <Link representation="button" trailingIcon="chevronRight" href={routes(locale, AB_NEWS_ARTICLE_SLUG[locale]).blogArticle}>
            {t('energyPartner.action')}
          </Link>
        )}
      </TextAndMedia>

      <CallToAction
        title={t('callToAction.title')}
        color="dark"
        shape="s3"
        action={<StartSimulationButton simulationSalesOffice={AB_SIMULATION_SALES_OFFICE} fromPage="ancienne-belgique" />}
      >
        <p>
          <Trans
            {...{ t }}
            i18nKey="callToAction.description"
            components={[
              <Link
                as="a"
                target="_blank"
                variant="inline"
                href={`mailto:${BOLT_EMAIL[locale]}`}
                onMouseDown={() =>
                  trackEvent(
                    { category: 'contact_us', object: 'email', action: 'click' },
                    { [CommonTrackingParams.FROM_PAGE]: 'ancienne-belgique' }
                  )
                }
              />
            ]}
          />
        </p>
      </CallToAction>

      {/* OFFERING */}
      <BoltOffering title={t('common:offering.title')} steps={USPs} customImage="website/ancienne-belgique/offering" />

      {/* CALL TO ACTION */}
      <HeadingAndContent title={t('friends.title')} color="accent-vibrant">
        <p>{parse(t('friends.description'))}</p>
        <Link representation="button" variant="layered" trailingIcon="chevronRight" href={routes(locale).referral}>
          {t('friends.action')}
        </Link>
      </HeadingAndContent>

      {/* FAQ */}
      <FaqWithDefaultDescription categories={faqCategories} title={t('faqTitle')} />
    </main>
  )
}

export default AncienneBelgique
